import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import usePersistentStateWithDefault from '../hooks/use-persistent-state-with-default';
import { RangeEnum } from '../model/range.enum';
import { lineChartColors } from '../theme/colors';
import Utils from '../utils/immersive_utils';

import usePersistentState from './use-persistent-state';
import { WebSocketContext } from './websocket-context/websocket-provider';
import { SUBSCRIPTIONS_KEY } from '../constants/websocket-events';
import { VolumeMultipleRequest } from './model/volumeData';
import { SnapshotRequest } from './model/snapshotData';
import { SnapshotTypeEnum } from '../model/snapshot_type';
import { GlobalContext } from './global-provider';
import { useSmartInactivityWake } from '../hooks/use-smart-inactivity-wake';
import { CandleRequest, CandleSubscriptionRequest } from './model/candleData';

const KEY = 'TIMESERIES';

interface ITimeSeriesProvider {
  children: ReactNode;
  providerKey: string;
  isOnlyPerps?: boolean;
}

export enum RequestTypeEnum {
  VOLS = 'VOLS',
  PERPETUAL = 'PERPETUAL',
}

export interface PerpTokenData {
  token: string;
  source: string;
}

export interface Series {
  id: string;
  color: string;
  delta: string;
  label: string;
  tenor: string;
  currency: string;
  data: {
    time: number;
    value: number;
  }[];
  type?: RequestTypeEnum;
  error?: boolean;
  source?: string;
}

interface Subscribs {
  type: RequestTypeEnum;
  currency: string;
}

interface ITimeseriesContext {
  addSeries: (token: string, tenor: string, delta: string, type: RequestTypeEnum) => void;
  deleteSeries: (serie: Series) => void;
  series: Series[];
  isLoading: boolean;
  range: RangeEnum;
  setRange: (range: RangeEnum) => void;
  initTimeStamp: string;
  updateInitTimeStamp: () => void;
  seriesExists: (token: string, tenor: string, delta: string, type?: RequestTypeEnum) => boolean;
  perpsList: PerpTokenData[];
  fetchPerpsList: () => void;
  isRangeChanged: any;
  fetchingList: any;
  isOnlyPerps: boolean;
}

export const TimeseriesContext = createContext<ITimeseriesContext>({} as ITimeseriesContext);

export const TimeseriesProvider = (props: ITimeSeriesProvider) => {
  const { providerKey, isOnlyPerps = false } = props;

  const STATE_PATH = [KEY, providerKey];

  const isFirstLoadRef = useRef(false);
  const isRangeChangedRef = useRef(false);
  const { marketDataOverviewCurrentTab: currentTab } = useContext(GlobalContext);

  const [series, setSeries] = usePersistentStateWithDefault<Series[]>(
    STATE_PATH,
    'series',
    [],
    'data',
    [],
  );
  const [range, setRange] = usePersistentState(STATE_PATH, 'range', RangeEnum.D1);
  const [subscribs, setSubscribs] = useState<Subscribs[]>([]);
  const fetchingListRef = useRef<Set<string>>(new Set());

  const isLoading = fetchingListRef.current.size > 0;

  const [initTimeStamp, setInitTimeStamp] = useState<string>(
    Utils.getInitDateByRange(new Date(), RangeEnum.D1).toISOString(),
  );

  const [perpsList, setPerpsList] = useState<PerpTokenData[]>([]);

  const { subscribeToChannel, isConnected, sendMessage, unsubscribeFromChannel } =
    useContext(WebSocketContext);

  const generateCompositeId = (id: string, currency?: string) => {
    return Utils.generateCompositeIdWithPrefix(id, currency || '', providerKey);
  };

  const getNextColor = (usedColors: string[]) => {
    const availableColors = lineChartColors.filter((color) => !usedColors.includes(color));
    return availableColors.length > 0 ? availableColors[0] : '#000000';
  };

  const addFetchToList = (id: string) => {
    fetchingListRef.current.add(id);
  };
  const removeFetchFromList = (id: string) => {
    fetchingListRef.current.delete(id);
  };

  const updateSeriesWithError = (seriesToUpdate: Series | Series[]) => {
    setSeries((prevSeries) => {
      let seriesArray = Array.isArray(seriesToUpdate) ? seriesToUpdate : [seriesToUpdate];

      seriesArray = seriesArray.map((s) =>
        s.data && s.data.length > 0 ? s : { ...s, error: true },
      );

      const filteredList = prevSeries.filter(
        (s) => !seriesArray.some((errSerie) => errSerie.id === s.id),
      );
      return [...filteredList, ...seriesArray];
    });
  };

  const sanitizeSeriesData = (data: { time: number; value: number }[]) => {
    return data
      .filter((value, index, self) => self.findIndex((v) => v.time === value.time) === index)
      .sort((a, b) => a.time - b.time);
  };

  const updateSeries = (seriesToUpdate: Series | Series[], isRangeChanged: boolean) => {
    setSeries((prevSeries) => {
      const seriesArray = Array.isArray(seriesToUpdate) ? seriesToUpdate : [seriesToUpdate];

      const updatedSeries = seriesArray.reduce((acc, serie) => {
        const existingSerie = acc.find((s) => s.id === serie.id);

        if (existingSerie) {
          let mergedData;
          if (isRangeChanged) {
            mergedData = sanitizeSeriesData(serie.data);
          } else {
            mergedData = sanitizeSeriesData([...existingSerie.data, ...serie.data]);
          }

          return acc.map((s) => (s.id === serie.id ? { ...s, data: mergedData } : s));
        }

        return [...acc, serie];
      }, prevSeries);

      return updatedSeries;
    });
  };

  const getSerieFromResult = (resultItem: any[], index: number) => {
    if (resultItem && resultItem.length > index) {
      const raw = resultItem[0] as string;
      const iso = raw.includes(' ') ? raw.replace(' ', 'T') : raw;
      const time = new Date(iso + 'Z').getTime() / 1000;

      const col = index;
      const value = resultItem[col];

      if (value === null || value === undefined || isNaN(value)) return null;
      return { time, value };
    }
    return null;
  };

  const handleResponse = (seriesArr: Series[], response: any, isRangeChanged: boolean) => {
    const { result, error, id } = response;

    if (error || !result || !result.length) {
      updateSeriesWithError(seriesArr);
      removeFetchFromList(id);
      return;
    }

    const newItems = seriesArr.map((serie, idx) => {
      return {
        ...serie,
        data: result
          .map((r: any) => {
            const colIdx = serie.type === RequestTypeEnum.PERPETUAL ? r.length - 1 : idx + 1;
            return getSerieFromResult(r, colIdx);
          })
          .filter((d: any) => !!d),
      };
    });

    removeFetchFromList(id);
    updateSeries(newItems, isRangeChanged);
  };

  const makeRequest = (
    requestType: RequestTypeEnum,
    id: string,
    begin: Date,
    end: Date,
    interval: string,
    series: Series[],
    currency?: string,
    source?: string,
  ) => {
    const seriesVols = series.map((serie) => ({
      label: serie.delta,
      tenor: serie.tenor,
    }));

    switch (requestType) {
      case RequestTypeEnum.PERPETUAL:
        return new CandleRequest(
          id,
          begin.toISOString(),
          end.toISOString(),
          interval,
          source || 'DERIBIT',
          currency as string,
          'PERPETUAL',
        );
      case RequestTypeEnum.VOLS:
        return new VolumeMultipleRequest(
          id,
          begin.toISOString(),
          end.toISOString(),
          interval,
          'DERIBIT',
          currency as string,
          seriesVols,
          'SVI',
        );
      default:
        return null;
    }
  };

  const getSeriesData = useCallback(
    (
      requestType: RequestTypeEnum,
      beginDate: Date,
      endDate: Date,
      key: string,
      seriesArr: Series[],
      ra?: RangeEnum,
      currency?: string,
      isRangeChanged: boolean = false,
      source?: string,
    ) => {
      return new Promise((resolve, reject) => {
        const r = ra ?? range;

        const id = generateCompositeId(key, currency);

        const req = makeRequest(
          requestType,
          id,
          beginDate,
          endDate,
          Utils.getIntervalByRange(r),
          seriesArr,
          currency,
          source,
        );

        sendMessage(JSON.stringify(req));

        addFetchToList(id);
        subscribeToChannel(id, (response: any) => {
          try {
            handleResponse(seriesArr, response, isRangeChanged);
            resolve('success');
          } catch (error) {
            removeFetchFromList(id);
            reject(error);
          }
        });
      });
    },
    [initTimeStamp, sendMessage, range, series],
  );

  const groupSeriesBy = (series: any, key: keyof Series) => {
    return series.reduce(
      (acc: any, curr: any) => {
        const groupKey = curr[key];
        (acc[groupKey] = acc[groupKey] || []).push(curr);
        return acc;
      },
      {} as Record<string, Series[]>,
    );
  };

  const handleResponseSubscription = useCallback(
    (response: any, requestType: RequestTypeEnum, currency: string) => {
      const { params } = response;
      const { data } = params;

      const interval = Utils.getIntervalByRange(range);
      const now = new Date();
      now.setSeconds(0, 0);
      const nowUpdated = Utils.updateDateToInterval(now, interval);
      const receivedDate = new Date(data.timestamp + 'Z');
      receivedDate.setSeconds(0, 0);

      if (receivedDate.getTime() !== nowUpdated.getTime()) {
        return;
      }

      if (requestType === RequestTypeEnum.VOLS) {
        const timestamp = new Date(data.timestamp + 'Z').getTime() / 1000;
        const tenors = data.tenors;

        setSeries((prev) => {
          const updatedSeries = prev.map((serie) => {
            if (
              serie.type === RequestTypeEnum.VOLS &&
              tenors[serie.tenor] &&
              serie.currency === currency
            ) {
              const value = tenors[serie.tenor][serie.delta];
              if (value !== undefined) {
                const newData = [...serie.data, { time: timestamp, value }]
                  .filter(
                    (value, index, self) => self.findIndex((v) => v.time === value.time) === index,
                  )
                  .sort((a, b) => a.time - b.time);

                return { ...serie, data: newData };
              }
            }
            return serie;
          });

          return updatedSeries;
        });
      }

      if (requestType === RequestTypeEnum.PERPETUAL) {
        const timestamp = new Date(data[0][0] + 'Z').getTime() / 1000;
        const value = data[0][1];

        setSeries((prev) => {
          const updatedSeries = prev.map((serie) => {
            if (serie.type === RequestTypeEnum.PERPETUAL && serie.currency === currency) {
              const newData = [...serie.data, { time: timestamp, value }]
                .filter(
                  (value, index, self) => self.findIndex((v) => v.time === value.time) === index,
                )
                .sort((a, b) => a.time - b.time);

              return { ...serie, data: newData };
            }
            return serie;
          });

          return updatedSeries;
        });
      }
    },
    [series, range],
  );

  const makeSubscription = useCallback(
    (currency: string, type: RequestTypeEnum) => {
      const now = new Date();
      now.setSeconds(0);
      now.setMilliseconds(0);

      const key =
        type === RequestTypeEnum.VOLS
          ? SUBSCRIPTIONS_KEY.TIMESERIES_SUBSCRIPTION
          : SUBSCRIPTIONS_KEY.TIMESERIES_SUBSCRIPTION_CANDLE;

      const id = generateCompositeId(key, currency);

      const interval = Utils.getIntervalByRange(range);
      const date = Utils.updateDateToInterval(now, interval);

      const candleSubsReq = new CandleSubscriptionRequest(
        generateCompositeId(key, currency),
        date.toISOString(),
        interval,
        'DERIBIT',
        currency,
        'PERPETUAL',
      );

      const volsReq = new SnapshotRequest(id, currency, SnapshotTypeEnum.CM, now.toISOString());

      const req = type === RequestTypeEnum.VOLS ? volsReq : candleSubsReq;
      sendMessage(JSON.stringify(req));

      subscribeToChannel(id, (response: any) => {
        handleResponseSubscription(response, type, currency);
      });
    },
    [sendMessage, subscribeToChannel, series, range, handleResponseSubscription],
  );

  const validateSubscribs = (seriesArr: Series[]) => {
    const newSubscribs: Subscribs[] = [];
    const seriesByCurrency = groupSeriesBy(seriesArr, 'currency');

    Object.keys(seriesByCurrency).forEach((currency) => {
      const seriesByType = groupSeriesBy(seriesByCurrency[currency], 'type');

      Object.keys(seriesByType).forEach((type) => {
        newSubscribs.push({
          type: type as RequestTypeEnum,
          currency: currency,
        });
      });
    });

    const newSubscriptions = newSubscribs.filter(
      (sub) => !subscribs.some((s) => s.type === sub.type && s.currency === sub.currency),
    );

    const removedSubscriptions = subscribs.filter(
      (sub) => !newSubscribs.some((s) => s.type === sub.type && s.currency === sub.currency),
    );

    newSubscriptions.forEach((sub) => {
      makeSubscription(sub.currency, sub.type);
    });

    removedSubscriptions.forEach((sub) => {
      const key =
        sub.type === RequestTypeEnum.VOLS
          ? SUBSCRIPTIONS_KEY.TIMESERIES_SUBSCRIPTION
          : SUBSCRIPTIONS_KEY.TIMESERIES_SUBSCRIPTION_CANDLE;

      const id = generateCompositeId(key, sub.currency);

      unsubscribeFromChannel(id);
    });

    setSubscribs(newSubscribs);
  };

  const makeSerieId = (token: string, tenor: string, delta: string, type: RequestTypeEnum) => {
    if (type === RequestTypeEnum.PERPETUAL) {
      return `${type}_${token}_${type}`;
    }

    return `${type}_${token}_${tenor}_${delta}`;
  };

  const makeLabel = (token: string, tenor: string, delta: string, type: RequestTypeEnum) => {
    if (type === RequestTypeEnum.PERPETUAL) {
      return `${type} ${token}`;
    }

    return `${token} ${tenor} ${delta}`;
  };

  const getEarliestSeriesTimestamp = (): number | null => {
    if (!series || series.length === 0) return null;

    let earliestTimestamp: number | null = null;

    series.forEach((serie) => {
      if (serie.data && serie.data.length > 0) {
        const serieEarliestTime = Math.min(...serie.data.map((d) => d.time));

        if (earliestTimestamp === null || serieEarliestTime < earliestTimestamp) {
          earliestTimestamp = serieEarliestTime;
        }
      }
    });

    return earliestTimestamp;
  };

  const addSeries = (token: string, tenor: string, delta: string, type: RequestTypeEnum) => {
    const minTimestamp = getEarliestSeriesTimestamp();

    const endDate = new Date();

    let initDate = Utils.getInitDateByRange(endDate, range);

    if (minTimestamp !== null) {
      initDate = new Date(minTimestamp * 1000);
    }

    const usedColors = series.map((s) => s.color);
    const newColor = getNextColor(usedColors);

    let source = '';

    if (type === RequestTypeEnum.PERPETUAL) {
      const perp = perpsList?.find((p: PerpTokenData) => p.token === token);
      source = perp?.source || '';
    } else {
      source = 'DERIBIT';
    }

    const seriesArr = [
      {
        id: makeSerieId(token, tenor, delta, type),
        label: makeLabel(token, tenor, delta, type),
        color: newColor,
        delta,
        tenor,
        currency: token,
        data: [],
        type,
        source,
      },
    ];

    getSeriesData(
      type,
      initDate,
      new Date(),
      type === RequestTypeEnum.PERPETUAL ? KEY : KEY + '_IMPLIED',
      seriesArr,
      range,
      token,
      false,
      source,
    );
  };

  const seriesExists = (
    token: string,
    tenor: string,
    delta: string,
    type: RequestTypeEnum = RequestTypeEnum.VOLS,
  ): boolean => {
    return series?.some((s) => s.id === makeSerieId(token, tenor, delta, type));
  };

  const deleteSeries = useCallback(
    (serie: Series) => {
      setSeries((prev) => {
        const nArr = prev.filter((s) => s.id !== serie.id);
        return nArr;
      });
    },
    [setSeries],
  );

  const resetSubscriptions = (seriesArr: Series[]) => {
    const seriesByCurrency = groupSeriesBy(seriesArr, 'currency');

    Object.keys(seriesByCurrency).forEach((currency) => {
      const seriesByType = groupSeriesBy(seriesByCurrency[currency], 'type');

      Object.keys(seriesByType).forEach((type) => {
        const key =
          type === RequestTypeEnum.VOLS
            ? SUBSCRIPTIONS_KEY.TOKEN_VOLATILITY_TIMESERIES_SUBSCRIPTION
            : SUBSCRIPTIONS_KEY.TOKEN_VOLATILITY_TIMESERIES_SUBSCRIPTION_CANDLE;

        const id = generateCompositeId(key, currency as string);
        unsubscribeFromChannel(id);
        makeSubscription(currency as string, type as RequestTypeEnum);
      });
    });
  };

  const fetchPerpertualData = useCallback(
    (perpetualSeries: Series[], initDate: Date, endDate: Date, rangeChanged: boolean) => {
      perpetualSeries.forEach((serie) => {
        getSeriesData(
          RequestTypeEnum.PERPETUAL,
          initDate,
          endDate,
          KEY,
          [serie],
          range,
          serie.currency,
          rangeChanged,
          serie.source,
        );
      });
    },
    [range],
  );

  const fetchVolSeries = (
    volSeries: Series[],
    initDate: Date,
    endDate: Date,
    range: RangeEnum,
    rangeChanged: boolean,
  ) => {
    const seriesByCurrency = groupSeriesBy(volSeries, 'currency');
    Object.keys(seriesByCurrency).forEach((currency) => {
      getSeriesData(
        RequestTypeEnum.VOLS,
        initDate,
        endDate,
        KEY + '_IMPLIED',
        seriesByCurrency[currency],
        range,
        currency as string,
        rangeChanged,
      );
    });
  };

  const getDates = (rangeChanged: boolean) => {
    let initDate = new Date();
    let endDate = new Date();

    const interval = Utils.getIntervalByRange(range);

    if (rangeChanged) {
      const newInitTimestamp = Utils.getInitDateByRange(new Date(), range).toISOString();

      initDate = new Date(newInitTimestamp);
      endDate = Utils.updateDateToInterval(new Date(), interval);
    } else {
      const earliestTimestamp = getEarliestSeriesTimestamp();

      let currentInitialDate = Utils.getInitDateByRange(new Date(), range);

      if (earliestTimestamp !== null) {
        currentInitialDate = new Date(earliestTimestamp * 1000);
      }

      initDate = Utils.getInitDateByRange(currentInitialDate, range);
      endDate = Utils.updateDateToInterval(currentInitialDate, interval);
    }

    return {
      initDate,
      endDate,
    };
  };

  const handleFetchSeries = useCallback(
    (rangeChanged = false) => {
      if (!isConnected || series.length === 0 || isLoading) return;

      const { initDate, endDate } = getDates(rangeChanged);

      const seriesWithoutErrors = series.filter((s) => !s.error);

      const seriesByType = groupSeriesBy(seriesWithoutErrors, 'type');
      const perpetualSeries = seriesByType?.[RequestTypeEnum.PERPETUAL];
      const volSeries = seriesByType?.[RequestTypeEnum.VOLS];

      if (perpetualSeries?.length > 0) {
        fetchPerpertualData(perpetualSeries, initDate, endDate, rangeChanged);
      }

      if (volSeries?.length > 0) {
        fetchVolSeries(volSeries, initDate, endDate, range, rangeChanged);
      }
    },
    [isConnected, series, isLoading, range, fetchPerpertualData, setInitTimeStamp],
  );

  const updateInitTimeStamp = () => {
    if (isLoading) return;
    handleFetchSeries();
  };

  const validateRangeChange = () => {
    const rangeChanged = true;
    isRangeChangedRef.current = true;
    handleFetchSeries(rangeChanged);

    resetSubscriptions(series);
  };

  const fetchPerpsList = useCallback(() => {
    const key = 'LIST_PERPS_TS_REQUEST';
    const id = generateCompositeId(key);
    const req = {
      jsonrpc: '2.0',
      id,
      method: 'list_prices',
      params: { source: [] },
    };
    sendMessage(JSON.stringify(req));
    subscribeToChannel(id, (response: any) => {
      try {
        const { result, error } = response;
        if (!error && result && result.sources) {
          const sourcesList = result.sources;
          const list = sourcesList.flatMap((source: any) => {
            const sourceName = Object.keys(source)?.[0];
            const tokens = source?.[sourceName]?.underlying;
            return tokens?.map((token: string) => ({ token, source: sourceName })) || [];
          });

          setPerpsList(list);
        }
      } catch {
        // no-op
      }
    });
  }, [sendMessage, subscribeToChannel, generateCompositeId]);

  useEffect(() => {
    if (isConnected && series.length > 0) {
      const seriesWithoutErrors = series.filter((s) => !s.error);

      validateSubscribs(seriesWithoutErrors);
    }
  }, [series, isConnected]);

  useEffect(() => {
    validateRangeChange();
    isRangeChangedRef.current = true;
  }, [range]);

  useEffect(() => {
    if (!isFirstLoadRef.current && isConnected) {
      if (series && series.length > 0) {
        isFirstLoadRef.current = true;

        handleFetchSeries(true);
      }
    }
  }, [series, range, isConnected, currentTab, providerKey]);

  useEffect(() => {
    if (!isConnected) {
      fetchingListRef.current = new Set();
    }
  }, [isConnected]);

  useSmartInactivityWake(() => {
    if (isConnected) {
      handleFetchSeries(true);
    }
  });

  return (
    <TimeseriesContext.Provider
      value={{
        addSeries,
        deleteSeries,
        series,
        isLoading,
        range,
        setRange,
        updateInitTimeStamp,
        seriesExists,
        initTimeStamp,
        perpsList,
        fetchPerpsList,
        fetchingList: fetchingListRef.current.size,
        isRangeChanged: isRangeChangedRef,
        isOnlyPerps,
      }}
    >
      {props.children}
    </TimeseriesContext.Provider>
  );
};
